import { ReactCarousel } from '@/components/react/ui/carousel/ReactCarousel';
import { ReactCarouselSlide } from '@/components/react/ui/carousel/ReactCarouselSlide';
import { ModalCloseCta } from '@/components/react/ui/modals/ModalCloseCta';
import { ReactModal } from '@/components/react/ui/modals/ReactModal';
import { ReactCta } from '@/components/react/ui/ReactCta';
import { ReactIcon } from '@/components/react/ui/ReactIcon';
import { ReactImage } from '@/components/react/ui/ReactImage';
import type { IconName } from '@/consts';
import { cx } from '@/cva.config';

interface QuickPreviewModalProps {
  closeText: string;
  cta: {
    href: string;
    text: string;
  };
  description: string;
  details: {
    icon: IconName;
    text: string;
  }[];
  images: ImageMetadata[];
  name: string;
}

export const QuickPreviewModal = ({
  closeText,
  cta,
  description,
  details,
  images,
  name,
}: QuickPreviewModalProps) => (
  <ReactModal popup="quickPreview">
    <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
      <CloseButton closeText={closeText} />
      <div className="flex aspect-square items-center justify-center md:max-lg:h-117 lg:h-126">
        <ReactCarousel
          controlOptions={{
            iconShape: 'chevron',
            position: 'overlaySlides',
            theme: 'dark',
            size: 'large',
          }}
          dotOptions={{
            position: 'overlaySlides',
          }}
        >
          {images.map((image, index) => (
            <ReactCarouselSlide key={index} intent="quickPreview">
              <ReactImage {...image} />
            </ReactCarouselSlide>
          ))}
        </ReactCarousel>
      </div>
      <div className="flex w-full flex-col gap-5.5">
        <CloseButton closeText={closeText} desktop />
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4">
            <h4>{name}</h4>
            <div className="text-blurb-text-subtle">{description}</div>
            <div className="flex flex-col items-start gap-2 self-stretch">
              {details.map(({ icon, text }, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 self-stretch"
                >
                  <ReactIcon name={icon} size="large" />
                  <div className="text-blurb-text-subtle">{text}</div>
                </div>
              ))}
            </div>
            <ReactCta
              className="max-lg:w-full"
              href={cta.href}
              fullWidth="mobileAndTabletOnly"
              intent="promo"
              size="large"
              text={cta.text}
              variant="filled"
            />
          </div>
        </div>
      </div>
    </div>
  </ReactModal>
);

function CloseButton({
  closeText,
  desktop = false,
}: {
  closeText: string;
  desktop?: boolean;
}) {
  return (
    <div
      className={cx('items-start justify-end self-stretch', {
        'hidden lg:flex': desktop,
        'flex lg:hidden': !desktop,
      })}
    >
      <ModalCloseCta closeText={closeText} />
    </div>
  );
}
